import { Location } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, ValidatorFn, Validators } from '@angular/forms';
import { EMPTY, merge, Subject, timer } from 'rxjs';
import { catchError, filter, finalize, map, pairwise, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { Dayreport } from 'src/app/classes/dayreport.class';
import { Material } from 'src/app/classes/material.class';
import { Planning } from 'src/app/classes/planning.class';
import { Status } from 'src/app/classes/status.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { DayreportService } from 'src/app/services/dayreport.service';
import { DayReportFieldGroups, EntityId, EntityTypeCode, FieldType } from 'src/app/services/entities/entity-type.class';
import { EntityFormField } from 'src/app/services/entity-type.service';
import { MaterialService } from 'src/app/services/material/material.service';
import { StatusService } from 'src/app/services/planning/status.service';
import { WeatherService } from 'src/app/services/weather/weather.service';
import { LocalStorage } from 'src/app/storage.class';
import { Utils } from 'src/app/utils.class';
import { baseDayReportFields } from 'src/environments/trackline/entitytypes';
import { DagrapportStatus } from 'src/environments/trackline/status.dagrapport';
import { DayReportStateService } from '../day-report-state.service';

export interface DayReportFieldEntry {
    key: string;
    field: EntityFormField;
}

export interface DayReportFieldPair {
    label: string;
    entry1: DayReportFieldEntry;
    entry2: DayReportFieldEntry;
}

export interface DayReportFormGroup {
    key: string;
    label: string;
    fields: DayReportFieldEntry[];
    /** Populated when all fields come in _1/_2 pairs */
    pairs: DayReportFieldPair[] | null;
}

@Component({
    selector: 'app-day-report-form',
    standalone: false,
    templateUrl: './day-report-form.component.html',
    styleUrl: './day-report-form.component.scss',
})
export class DayReportFormComponent extends CodaltComponent implements OnInit {
    saving = false;
    saved = false;
    user = LocalStorage.getUser();

    private weatherService = inject(WeatherService);
    private statusService = inject(StatusService);
    private materialService = inject(MaterialService);
    private statuses: Status[] = [];
    private weatherMetingReset$ = new Subject<1 | 2>();
    private autofillCancelled$ = new Subject<void>();
    private manuallyUnlockedFCs = new Set<FormControl>();

    readonly weatherLoading = signal(new Set<1 | 2>());
    readOnly = true;

    statusControl = new FormControl<string | null>(null);
    statusField: EntityFormField = {
        name: 'Status',
        type: FieldType.StatusKey,
        statusType: 'dagrapport',
        fc: this.statusControl,
    };

    commentControl = new FormControl<string | null>(null);
    commentField: EntityFormField = {
        name: 'Opmerking',
        type: FieldType.Textarea,
        fc: this.commentControl,
    };

    planning: Planning | null = null;
    dayReport: Dayreport | null = null;
    materials: Material[] = [];
    materialTypes: { name: string; count: number }[] = [];

    groups: DayReportFormGroup[] = [];
    autofilledFCs = new Set<FormControl>();

    constructor(
        private stateService: DayReportStateService,
        private dayreportService: DayreportService,
        public location: Location,
        protected confirmDialog: ConfirmDialogService
    ) {
        super();
    }

    ngOnInit(): void {
        this.groups = this.buildGroups(baseDayReportFields);

        this.setupWeatherAutofill();

        this.setupThermoTijdAutofill();

        this.subscriptions.add(
            this.materialService.getList(true).subscribe(materials => {
                this.materials = materials;

                this.updateMaterialTypesAndApplicationMethod();
            })
        );

        this.subscriptions.add(
            this.statusService.getList('dagrapport').subscribe(statuses => {
                this.statuses = statuses;
                this.applyReadOnlyState();
            })
        );

        this.subscriptions.add(
            this.stateService.planning$.subscribe(planning => {
                if (this.planning?.id !== planning?.id) {
                    this.autofillCancelled$.next();
                }

                this.planning = planning;

                this.applyReadOnlyState();
            })
        );

        // Moet vóór de dayReport$-subscribe hieronder staan: die zet statusControl's waarde
        // (ook bij het eerste, synchrone bericht), en dat moet de required-validator op
        // commentControl direct meenemen.
        this.subscriptions.add(
            this.statusControl.valueChanges.subscribe(statusKey => {
                if (this.statuses.find(status => status.key === statusKey)?.mutable === true) {
                    this.applyReadOnlyState(statusKey);
                }

                this.applyCommentRequired(statusKey);
            })
        );

        this.subscriptions.add(
            this.stateService.dayReport$.subscribe(dayReport => {
                if (dayReport && dayReport.planning_id !== this.planning?.id) {
                    return;
                }

                const initialLoad = !this.dayReport || this.dayReport.planning_id !== dayReport?.planning_id;

                this.dayReport = dayReport;

                if (initialLoad) {
                    this.autofillCancelled$.next();
                    this.autofilledFCs.clear();
                    this.manuallyUnlockedFCs.clear();

                    for (const group of this.groups) {
                        for (const { field } of group.fields) {
                            field.fc.reset(field.default ?? null, { emitEvent: false });

                            if (field.type === FieldType.BaseDateTime) {
                                this.autofilledFCs.add(field.fc);
                            }
                        }
                    }

                    this.getFormControls().forEach(fc => {
                        fc.markAsPristine();
                        fc.markAsUntouched();
                    });
                }

                if (!dayReport) {
                    this.materialTypes = [];

                    // Nog geen dagrapport voor deze planning: alvast tonen wat het bij opslaan
                    // sowieso wordt (kolomdefault 'dagrapportGepland' op de dayreport-tabel).
                    this.statusControl.setValue(DagrapportStatus.DagrapportGepland);
                    this.commentControl.setValue(null);

                    return;
                }

                this.statusControl.setValue(dayReport.status_key ?? null);
                this.commentControl.setValue(dayReport.comment ?? null, { emitEvent: false });

                const data = dayReport.form_data ?? {};

                for (const group of this.groups) {
                    for (const { key, field } of group.fields) {
                        if (data[key] !== undefined) {
                            field.fc.setValue(data[key], { emitEvent: false });
                        }
                    }
                }

                const planningStart = this.planning?.mainPlanning?.begindate;
                const planningEnd = this.planning?.mainPlanning?.enddate;
                const createdAt = dayReport.created_at ? new Date(dayReport.created_at) : null;

                if (
                    initialLoad &&
                    createdAt &&
                    planningStart &&
                    planningEnd &&
                    createdAt >= new Date(planningStart) &&
                    createdAt <= new Date(planningEnd)
                ) {
                    for (const group of this.groups) {
                        for (const { key, field } of group.fields) {
                            if (field.type === FieldType.BaseDateTime && !field.fc.value) {
                                field.fc.setValue(key.endsWith('_1') ? planningStart : planningEnd, {
                                    emitEvent: false,
                                });
                            }
                        }
                    }
                }

                this.updateMaterialTypesAndApplicationMethod();

                // Disable weather-autofilled fields if wegdek was already filled
                const tempGroup = this.groups.find(g => g.key === 'temp_vochtigheid');
                if (initialLoad && tempGroup) {
                    const getFC = (key: string) => tempGroup.fields.find(f => f.key === key)?.field.fc;

                    ([1, 2] as const).forEach(meting => {
                        if (getFC(`vocht_wegdek_${meting}`)?.value) {
                            [
                                `vocht_lucht_${meting}`,
                                `vocht_luchtvochtigheid_${meting}`,
                                `vocht_dauwpunt_${meting}`,
                                `vocht_tijd_${meting}`,
                            ].forEach(key => {
                                const fc = getFC(key);

                                if (fc) {
                                    this.autofilledFCs.add(fc);
                                }
                            });
                        }
                    });
                }

                if (initialLoad) {
                    this.weatherMetingReset$.next(1);
                    this.weatherMetingReset$.next(2);
                }
                this.applyReadOnlyState();
            })
        );
    }

    // Wanneer de verrekening (declaratie) van dit dagrapport al definitief is, mag de status
    // niet meer teruggezet worden - anders dan bij de gewone readOnly-staat blijft de
    // statusdropdown dan ook zelf disabled (die is normaal juist altijd bewerkbaar, zodat de
    // gebruiker de status kan wijzigen om weer bewerkbaar te worden).
    get declarationLocked(): boolean {
        return this.dayReport?.declaration?.status?.mutable === false;
    }

    get hasMixRegistrationMaterial(): boolean {
        // Velucryl is voorwaarde voor registratie menging en dan verplicht (mag keihard op ID van product).
        // Als die er niet is, mag dat hele blok verborgen. 85 is ID van Velucryl
        return this.dayReport?.article_dayreports?.some(dr => dr.material_id === 85) ?? false;
    }

    private updateMaterialTypesAndApplicationMethod(): void {
        const counts = new Map<string, number>();

        this.dayReport?.article_dayreports?.forEach(articleDayreport => {
            this.materials
                .find(material => material.id === articleDayreport.material_id)
                ?.raw_materials?.filter(rawMaterial => rawMaterial.material_type?.main)
                .forEach(rawMaterial => {
                    const name = rawMaterial.material_type!.name.toLowerCase();
                    counts.set(name, (counts.get(name) ?? 0) + 1);
                });
        });

        this.materialTypes = Array.from(counts, ([name, count]) => ({ name, count }));

        const verfCount = counts.get('verf') ?? 0;
        const thermoplastCount = counts.get('thermoplast') ?? 0;
        const firstMatchingType = this.materialTypes.find(
            ({ name }) => name === 'verf' || name === 'thermoplast'
        )?.name;
        let applicationMethod: 'spuit' | 'slof' | null = null;

        if (verfCount > thermoplastCount) {
            applicationMethod = 'spuit';
        } else if (thermoplastCount > verfCount) {
            applicationMethod = 'slof';
        } else if (verfCount > 0) {
            applicationMethod = firstMatchingType === 'verf' ? 'spuit' : 'slof';
        }

        if (!applicationMethod) {
            return;
        }

        this.groups
            .flatMap(group => group.fields)
            .find(({ key }) => key === 'applicatie_methode')
            ?.field.fc.setValue(applicationMethod);
    }

    get formInvalid(): boolean {
        return this.getFormControls().some(control => control.invalid);
    }

    private applyReadOnlyState(statusKey = this.statusControl.value): void {
        const status =
            this.statuses.find(status => status.key === statusKey) ??
            (this.dayReport?.status_key === statusKey ? this.dayReport?.status : undefined);

        this.readOnly = this.declarationLocked || status?.mutable !== true;

        if (this.readOnly) {
            this.autofillCancelled$.next();
        }

        this.groups.forEach(group =>
            group.fields.forEach(({ field }) => {
                if (
                    this.readOnly ||
                    this.autofilledFCs.has(field.fc) ||
                    (group.key === 'registratie_menging' && !this.hasMixRegistrationMaterial)
                ) {
                    field.fc.disable({ emitEvent: false });
                } else {
                    field.fc.enable({ emitEvent: false });

                    if (group.key === 'registratie_menging' && field.fc.invalid) {
                        field.fc.markAsTouched();
                    }
                }
            })
        );

        if (this.declarationLocked) {
            this.statusControl.disable({ emitEvent: false });
        } else {
            this.statusControl.enable({ emitEvent: false });
        }

        if (this.readOnly) {
            this.commentControl.disable({ emitEvent: false });
        } else {
            this.commentControl.enable({ emitEvent: false });
        }
    }

    private applyCommentRequired(statusKey: string | null): void {
        const commentRequired = statusKey === DagrapportStatus.DagrapportGedeeltelijkAfgerond;

        this.commentControl.setValidators(commentRequired ? [Validators.required] : []);

        this.commentControl.updateValueAndValidity({ emitEvent: false });

        if (commentRequired) {
            this.commentControl.markAsTouched();
        }
    }

    /**
     * Show only planned times, including planned travel time.
     */
    get medewerkers(): string[] {
        return [...(this.planning?.user_planning ?? []), ...(this.planning?.planning_has ?? [])]
            .map(employee => {
                let name = 'entitytype_key' in employee ? employee.driver_user?.name : employee.user?.name;
                // Show hired employees (HiringEmployee) under personnel using their planned name.
                if ('entitytype_key' in employee && employee.entitytype_key === EntityTypeCode.InhuurPersoneel) {
                    name = employee.name;
                }
                const begin = employee.begindate ? Utils.formatDate(employee.begindate, 'HH:mm') : '';

                const end = employee.enddate ? Utils.formatDate(employee.enddate, 'HH:mm') : '';

                const time = begin ? ` (${[begin, end].filter(Boolean).join('-')})` : '';

                return name ? `${name}${time}` : '';
            })
            .filter(Boolean);
    }

    get entities() {
        // Exclude hired employees (HiringEmployee) from equipment.
        return (this.planning?.planning_has ?? []).filter(
            ph =>
                ph.entity_id &&
                // The main planning entry (e.g. Planning Stralen) is not equipment.
                ph.id !== this.planning.planning_has_id &&
                ph.entity_id !== EntityId.HiringEmployee &&
                ph.entitytype_key !== EntityTypeCode.InhuurPersoneel
        );
    }

    openChangesBackActionCheck(): boolean | Promise<boolean> {
        if (!this.hasUnsavedChanges()) {
            return true;
        }

        return this.confirmDialog
            .confirm({
                title: 'Niet opgeslagen wijzigingen',
                text: `Wilt u de niet opgeslagen wijzigingen verwerpen?`,
                confirm: 'Hier blijven',
                cancel: 'Wijzigingen verwerpen',
            })
            .then(() => false)
            .catch(() => true);
    }

    protected hasUnsavedChanges(): boolean {
        return this.getFormControls().some(control => control.dirty);
    }

    private getFormControls(): FormControl[] {
        return [
            this.statusControl,
            this.commentControl,
            ...this.groups.flatMap(group => group.fields.map(({ field }) => field.fc)),
        ];
    }

    private markFormAsSaved(): void {
        this.getFormControls().forEach(control => {
            control.markAsPristine();
            control.markAsUntouched();
        });

        setTimeout(() => (this.saved = false), 2500);
    }

    save() {
        if (!this.dayReport || this.saving) {
            return;
        }

        if (this.formInvalid) {
            this.getFormControls().forEach(control => control.markAsTouched());

            return;
        }

        this.saving = true;

        const form_data: Record<string, any> = {};
        for (const group of this.groups) {
            for (const { key, field } of group.fields) {
                form_data[key] = field.fc.value;
            }
        }

        const updatedDayReport = {
            ...this.dayReport,
            status_key: this.statusControl.value,
            comment: this.commentControl.value,
            form_data,
        } as Dayreport;

        this.subscriptions.add(
            this.dayreportService.save(updatedDayReport).subscribe({
                next: response => {
                    this.stateService.updateDayReport(response.data);
                    this.saved = true;
                    this.saving = false;
                    this.markFormAsSaved();
                },
                error: error => {
                    this.saving = false;
                    this.confirmDialog.confirm({
                        title: 'Opslaan niet gelukt',
                        text: error?.message || 'Het dagrapport kon niet worden opgeslagen. Probeer het opnieuw.',
                        confirm: 'Sluiten',
                        cancel: null,
                    });
                },
            })
        );
    }

    private setupWeatherAutofill(): void {
        const tempGroup = this.groups.find(g => g.key === 'temp_vochtigheid');

        if (!tempGroup) {
            return;
        }

        const getFC = (key: string) => tempGroup.fields.find(f => f.key === key)?.field.fc;

        ([1, 2] as const).forEach(meting => {
            const roadSurface = getFC(`vocht_wegdek_${meting}`);
            const timeFC = getFC(`vocht_tijd_${meting}`);

            if (!roadSurface || !timeFC) {
                return;
            }

            const isEmpty = (value: unknown) => value === null || value === '' || value === undefined;

            const weatherFieldKeys = [
                `vocht_lucht_${meting}`,
                `vocht_luchtvochtigheid_${meting}`,
                `vocht_dauwpunt_${meting}`,
            ];

            const weatherFieldCleared$ = merge(...weatherFieldKeys.map(key => getFC(key)?.valueChanges ?? EMPTY)).pipe(
                filter(isEmpty)
            );

            const reset$ = this.weatherMetingReset$.pipe(filter(resetMeting => resetMeting === meting));

            const initialRoadSurfaceValue$ = reset$.pipe(
                startWith(meting),
                switchMap(() =>
                    roadSurface.valueChanges.pipe(
                        startWith(roadSurface.value),
                        pairwise(),
                        filter(([previousValue, value]) => isEmpty(previousValue) && !isEmpty(value)),
                        take(1)
                    )
                )
            );

            this.subscriptions.add(
                merge(initialRoadSurfaceValue$, timeFC.valueChanges)
                    .pipe(
                        filter(() => !this.readOnly),
                        switchMap(() => timer(300).pipe(takeUntil(merge(reset$, this.autofillCancelled$)))),
                        filter(() => !this.readOnly && !!this.planning?.location),
                        switchMap(() => {
                            this.weatherLoading.update(loading => new Set(loading).add(meting));

                            const beginDate = this.planning?.mainPlanning?.begindate;
                            const endDate = this.planning?.mainPlanning?.enddate;

                            const now = new Date();

                            const dateTime = timeFC.value
                                ? new Date(timeFC.value)
                                : beginDate && endDate && now >= new Date(beginDate) && now <= new Date(endDate)
                                  ? now
                                  : beginDate
                                    ? new Date(beginDate)
                                    : undefined;

                            return this.weatherService
                                .getCurrentWeatherForLocation(
                                    this.planning!.location,
                                    this.planning?.location_data,
                                    dateTime
                                )
                                .pipe(
                                    map(response => ({ response, dateTime })),
                                    takeUntil(
                                        merge(
                                            timeFC.valueChanges,
                                            weatherFieldCleared$,
                                            reset$,
                                            this.autofillCancelled$
                                        )
                                    ),
                                    catchError(() => EMPTY),
                                    finalize(() =>
                                        this.weatherLoading.update(loading => {
                                            const next = new Set(loading);

                                            next.delete(meting);

                                            return next;
                                        })
                                    )
                                );
                        }),
                        filter(() => !this.readOnly)
                    )
                    .subscribe(({ response, dateTime }) => {
                        const weather = response.data;
                        const round1 = (n: number) => Math.round(n * 10) / 10;

                        if (this.autofilledFCs.has(timeFC) && !timeFC.value) {
                            timeFC.setValue(dateTime ?? new Date(weather.currentTime), { emitEvent: false });
                        }

                        const autofill: [string, number][] = [
                            [`vocht_lucht_${meting}`, round1(weather.temperature.degrees)],
                            [`vocht_luchtvochtigheid_${meting}`, Math.round(weather.relativeHumidity)],
                            [`vocht_dauwpunt_${meting}`, round1(weather.dewPoint.degrees)],
                        ];

                        for (const [key, value] of autofill) {
                            const fc = getFC(key);

                            if (fc && !this.manuallyUnlockedFCs.has(fc)) {
                                fc.setValue(value, { emitEvent: false });
                                this.autofilledFCs.add(fc);
                            }
                        }
                        this.applyReadOnlyState();
                    })
            );
        });
    }

    unlockFields(fcs: FormControl[]): void {
        if (this.readOnly) {
            return;
        }

        fcs.forEach(fc => {
            this.manuallyUnlockedFCs.add(fc);
            this.autofilledFCs.delete(fc);
        });

        this.applyReadOnlyState();
    }

    resetMeting(group: DayReportFormGroup, meting: 1 | 2): void {
        if (this.readOnly || !group.pairs) {
            return;
        }

        const resetWeatherMeting = group.key === 'temp_vochtigheid';

        group.pairs.forEach(pair => {
            const { field } = meting === 1 ? pair.entry1 : pair.entry2;
            const fc = field.fc;

            fc.setValue(null, { emitEvent: false });

            this.manuallyUnlockedFCs.delete(fc);

            if (field.type === FieldType.BaseDateTime) {
                // Terug op slot tot de bijbehorende meting opnieuw is ingevuld, zie buildGroups().
                this.autofilledFCs.add(fc);
            } else {
                this.autofilledFCs.delete(fc);
            }
        });

        this.applyReadOnlyState();

        if (resetWeatherMeting) {
            this.weatherMetingReset$.next(meting);
        }
    }

    private setupThermoTijdAutofill(): void {
        const thermoGroup = this.groups.find(g => g.key === 'temp_thermoplast');
        if (!thermoGroup) {
            return;
        }

        const getFC = (key: string) => thermoGroup.fields.find(f => f.key === key)?.field.fc;

        ([1, 2] as const).forEach(meting => {
            const triggerKeys = [`thermo_moederketel_${meting}`, `thermo_machine_${meting}`, `thermo_slof_${meting}`];

            triggerKeys.forEach(triggerKey => {
                const fc = getFC(triggerKey);

                if (!fc) {
                    return;
                }

                this.subscriptions.add(
                    fc.valueChanges
                        .pipe(
                            filter(v => v !== null && v !== '' && v !== undefined),
                            filter(() => !this.readOnly),
                            filter(() => {
                                const tijdFC = getFC(`thermo_tijd_${meting}`);

                                return !!tijdFC && !tijdFC.value && this.autofilledFCs.has(tijdFC);
                            })
                        )
                        .subscribe(() => {
                            const tijdFC = getFC(`thermo_tijd_${meting}`);
                            if (tijdFC) {
                                const now = new Date();
                                tijdFC.setValue(now, { emitEvent: false });

                                this.applyReadOnlyState();
                            }
                        })
                );
            });
        });
    }

    private buildGroups(dayReportFields: DayReportFieldGroups): DayReportFormGroup[] {
        return Object.entries(dayReportFields).map(([groupKey, group]) => {
            const fields: DayReportFieldEntry[] = Object.entries(group.fields)
                .sort(([, a], [, b]) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
                .map(([fieldKey, entityField]) => {
                    const fc = new FormControl(entityField.default ?? null, this.getValidators(entityField));

                    // Tijd-velden vullen zichzelf pas als de bijbehorende meting is ingevuld (zie
                    // setupWeatherAutofill/setupThermoTijdAutofill) - tot die tijd op slot, zodat je
                    // niet in de verleiding komt om zelf alvast "Nu" in te vullen.
                    if (entityField.type === FieldType.BaseDateTime) {
                        this.autofilledFCs.add(fc);
                    }

                    return {
                        key: fieldKey,
                        field: { ...entityField, fc } as EntityFormField,
                    };
                });

            const entries1 = fields.filter(e => e.key.endsWith('_1'));
            const entries2 = fields.filter(e => e.key.endsWith('_2'));

            const pairs: DayReportFieldPair[] = entries1
                .map(e1 => {
                    const baseKey = e1.key.slice(0, -2);
                    const e2 = entries2.find(e => e.key === baseKey + '_2');

                    if (!e2) return null;

                    return {
                        label: e1.field.name,
                        entry1: e1,
                        entry2: e2,
                    };
                })
                .filter((p): p is DayReportFieldPair => p !== null);

            const isPaired = pairs.length > 0 && pairs.length * 2 === fields.length;

            return {
                key: groupKey,
                label: group.label,
                fields,
                pairs: isPaired ? pairs : null,
            };
        });
    }

    private getValidators(entityField: any): ValidatorFn[] {
        const v = entityField.validators;

        return [
            v?.min !== undefined ? Validators.min(v.min) : null,
            v?.max !== undefined ? Validators.max(v.max) : null,
            v?.minLength !== undefined ? Validators.minLength(v.minLength) : null,
            v?.maxLength !== undefined ? Validators.maxLength(v.maxLength) : null,
            v?.pattern !== undefined ? Validators.pattern(v.pattern) : null,
            entityField.required ? Validators.required : null,
        ].filter((validator): validator is ValidatorFn => validator !== null);
    }
}
